import React from 'react';
import Firebase from 'firebase/app';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { DataGrid } from '@material-ui/data-grid';

import { RusherDialog } from '../common/Dialogs';
import { BlockWrapper } from '../../../../../styles/admin';
import { useTheme } from '../../../../../hooks';
import { updateRusher } from '../../../../../actions/rusher';
import BlockLayout from '../common/BlockLayout';
import StatCard from '../common/StatCard';
import BlockHeader from '../common/BlockHeader';

const columns = [
  {
    field: 'name',
    headerName: 'Nom',
    width: 200,
  },
  {
    field: 'id',
    headerName: 'ID',
    width: 300,
  },

  {
    field: 'isWorked',
    headerName: 'Actif',
    width: 150,
    align: 'center',
  },
  {
    field: 'available',
    headerName: 'Activé 🔐',
    width: 150,
    align: 'center',
  },
  {
    field: 'isVerified',
    headerName: 'Vérifié',
    width: 150,
    align: 'center',
  },
  {
    field: 'isPaidVAT',
    headerName: 'Soumis TVA',
    width: 150,
    align: 'center',
  },
  {
    field: 'percentage',
    headerName: 'Commission 💸',
    width: 150,
    align: 'center',
    valueFormatter: ({ value }) => `${value}%`,
  },
  {
    field: 'baseCity',
    headerName: 'Ville de base',
    width: 200,
  },
  {
    field: 'phone',
    headerName: 'Telephone',
    width: 300,
  },
];

const RusherBlock = props => {
  const [editDialog, setEditDialog] = React.useState(null);
  const theme = useTheme();

  const [rushers, rushersLoading, rushersError] = useCollectionData(
    Firebase.firestore().collection('rushers'),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  const onRowClick = params => {
    setEditDialog({ updatedData: null, data: params.row });
  };

  const onConfirmRusherUpdate = async () => {
    if (editDialog.data !== editDialog.updatedData) {
      updateRusher({
        id: editDialog.data.id,
        ...(editDialog.updatedData.isVerified
          ? {
              isVerified:
                editDialog.updatedData.isVerified === '❌' ? false : true,
            }
          : {}),
        ...(editDialog.updatedData.percentage
          ? {
              percentage: editDialog.updatedData.percentage,
            }
          : {}),
        ...(editDialog.updatedData.available
          ? {
              available:
                editDialog.updatedData.available === '❌' ? false : true,
            }
          : {}),
        ...(editDialog.updatedData.isPaidVAT
          ? {
              isPaidVAT:
                editDialog.updatedData.isPaidVAT === '❌' ? false : true,
            }
          : {}),
      });
    }
    setEditDialog(null);
  };

  const { rows, rushersCount, availablesCount } = React.useMemo(() => {
    if (rushersLoading || !rushers || !!rushersError) {
      return {
        rows: [],
        rushersCount: 0,
        availablesCount: 0,
      };
    }

    const rows = rushers.map(rusher => ({
      id: rusher.id,
      name: rusher.name,
      available: rusher.available ? '✅' : '❌',
      isWorked: rusher.isWorked ? '✅' : '❌',
      baseCity: rusher.baseCity,
      phone: rusher.phone,
      isVerified: rusher.isVerified ? '✅' : '❌',
      percentage: rusher.percentage,
      isPaidVAT: rusher.isPaidVAT ? '✅' : '❌',
      pictureURL: rusher.pictureURL,
    }));

    const rushersCount = rushers.length;
    const availablesCount = rushers.filter(rusher => rusher.available).length;

    return {
      rows,
      rushersCount,
      availablesCount,
    };
  }, [rushers, rushersLoading, rushersError]);

  if (rushersError) {
    throw new Error(rushersError.toString());
  }

  return (
    <BlockWrapper theme={theme}>
      <BlockHeader
        title="Les livreurs"
        path="/admin/dashboard/rushers"
        label={`Tous les livreurs`}
      />

      <BlockLayout
        statBlocks={
          <>
            <StatCard label={'Total'} value={rushersCount} />
            <StatCard label={'Actifs'} value={availablesCount} />
          </>
        }
      >
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          loading={rushersLoading}
          onRowClick={onRowClick}
          stickyHeader
        />
      </BlockLayout>

      {editDialog ? (
        <RusherDialog
          rusher={editDialog}
          setEditDialog={setEditDialog}
          isOpen={!!editDialog}
          onClose={() => setEditDialog(null)}
          onConfirm={onConfirmRusherUpdate}
          cantConfirm={!editDialog.updatedData}
          isLoading={rushersLoading}
        />
      ) : null}
    </BlockWrapper>
  );
};

export default RusherBlock;
